import { useAppQuery } from '@/hooks/useAppQuery';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { api } from '@/lib/api-client';
import type { Item } from '@/models/item/item';
import { Permissions } from '@/models/permission';

import { itemsQueryKey } from './queryKeys';

// Translations are returned only when `translations=true` is passed; without it
// the endpoint omits them. Pass `withTranslation` only where they're needed (item
// detail), not for gift/snapshot/bundle fetches.
export const getItem = async (id: string, withTranslation = false) => {
  const response = await api.get<Item>(`/v1/item/${id}`, {
    params: withTranslation ? { translations: true } : undefined,
  });
  return response.data;
};

/** Shared by the hook and the route loaders that warm it, so both hit one cache entry. */
export const itemQuery = (id: string, withTranslation = false) => ({
  // Variant-aware key so translated/non-translated fetches don't collide.
  // `detail(id)` stays a prefix, so invalidation still matches both variants.
  queryKey: [...itemsQueryKey.detail(id), withTranslation],
  queryFn: () => getItem(id, withTranslation),
});

export const useGetItem = (id = '', withTranslation = false) => {
  const canReadItem = usePermissionCheck({
    oneOf: [Permissions.items.read],
  });
  return useAppQuery({
    ...itemQuery(id, withTranslation),
    enabled: !!id && canReadItem,
  });
};
